// src/components/legal/PrivacyPage.js — public /privacy page (C1, C3).
import { LegalDocShell } from "./LegalDocShell";

export const PrivacyPage = () => (
  <LegalDocShell
    docKey="privacy"
    title="Privacy Policy"
    description="Sahod Privacy Policy — how Sahod handles personal data under the Philippine Data Privacy Act (RA 10173)."
  >
    <div className="legal-doc__lede">
      This Privacy Policy explains how Sahod handles personal data. Sahod is a payroll and
      workforce-management platform for businesses operating in the Philippines, accessible at{" "}
      <strong>sahod.solutions</strong> (the &ldquo;Service&rdquo;). The Service is operated by
      Sahod.Solutions, a company incorporated in Australia (&ldquo;Sahod&rdquo;,
      &ldquo;we&rdquo;, &ldquo;us&rdquo;). Because we process the personal data of individuals in
      the Philippines, we handle that data in accordance with the Philippine{" "}
      <strong>Data Privacy Act of 2012 (RA 10173)</strong> and its Implementing Rules, and where
      applicable the Australian <strong>Privacy Act 1988</strong>.
    </div>

    <h2>1. Our two roles: controller and processor</h2>
    <p>
      Your relationship with us depends on whose data is involved, and this distinction governs
      the rest of this policy.
    </p>
    <ul>
      <li>
        <strong>For Customer account data</strong> &mdash; the information about the business
        that subscribes to Sahod and the individuals who administer the account (e.g. the person
        who signs up, billing contacts, admins and viewers) &mdash; Sahod is the{" "}
        <strong>Personal Information Controller</strong> (PIC). We decide why and how this data is
        processed.
      </li>
      <li>
        <strong>For Employee data</strong> &mdash; the workforce records a Customer enters into
        the Service to run payroll &mdash; the <strong>Customer is the Controller (PIC)</strong>{" "}
        and Sahod is the <strong>Personal Information Processor</strong> (PIP). We process this
        data only on the Customer&rsquo;s documented instructions and do not use it for our own
        purposes.
      </li>
    </ul>
    <p>
      If you are an employee of a business that uses Sahod, your employer &mdash; not Sahod
      &mdash; controls your data. To exercise your rights, contact your employer first (see
      Section 8).
    </p>

    <h2>2. Personal data we collect</h2>
    <h3>(a) Customer account data &mdash; we are the controller</h3>
    <ul>
      <li>Name, work email, mobile number, and role (Superuser, Admin, Viewer);</li>
      <li>Company details: business name, address, tax registration, company government IDs;</li>
      <li>Billing information (handled by our payment processor &mdash; see Section 4);</li>
      <li>Account credentials and authentication data.</li>
    </ul>
    <h3>(b) Employee data &mdash; we process on the Customer&rsquo;s behalf</h3>
    <p>
      When a Customer uses Sahod to manage its workforce, the Customer inputs data about its
      employees. This includes <strong>sensitive personal information</strong> within the meaning
      of RA 10173:
    </p>
    <ul>
      <li>Full name, date of birth, sex, marital status, residential address, contact details;</li>
      <li>Government identifiers: SSS, TIN, PhilHealth, and Pag-IBIG numbers;</li>
      <li>Bank account details for salary disbursement;</li>
      <li>Compensation, salary rates, deductions, loans, and payslip data;</li>
      <li>Time records, attendance, schedules, leave, overtime, and night-differential data;</li>
      <li>Employment history and status.</li>
    </ul>
    <h3>(c) Technical and usage data</h3>
    <ul>
      <li>IP address, device and browser information, and access logs;</li>
      <li>Audit-trail records of actions taken in the Service (for security and compliance).</li>
    </ul>
    <h3>(d) Prospect data</h3>
    <p>
      When you book a Discovery Session, we collect the contact and business details you enter,
      use them only to prepare for and follow up on the session, and delete them within 12 months
      if you don&rsquo;t become a Customer.
    </p>

    <h2>3. How we use personal data</h2>
    <p>
      We use account data to provide, secure, bill for, and support the Service, to communicate
      with you about it, and to comply with our legal obligations. We process employee data{" "}
      <strong>only to provide the Service to the Customer</strong> &mdash; to calculate and run
      payroll, generate payslips and bank files, and produce records &mdash; strictly on the
      Customer&rsquo;s instructions. We do not sell personal data, and we do not use employee data
      for advertising or to train unrelated products.
    </p>

    <h2>4. Disclosure and sub-processors</h2>
    <p>
      We do not disclose personal data to third parties except to the service providers below,
      who process data on our behalf under contractual confidentiality and security obligations,
      and to government authorities where required by law.
    </p>
    <table className="legal-doc__table">
      <tbody>
        <tr>
          <th>Function</th>
          <th>Data processed</th>
          <th>Processing location</th>
        </tr>
        <tr>
          <td>Cloud infrastructure (application hosting, database, storage, compute)</td>
          <td>Account and employee data</td>
          <td>Singapore</td>
        </tr>
        <tr>
          <td>Authentication and identity</td>
          <td>Account identifiers (email addresses, user IDs)</td>
          <td>United States</td>
        </tr>
        <tr>
          <td>Payment processing (PCI-DSS certified)</td>
          <td>Billing details (we do not store full card numbers)</td>
          <td>Provider&rsquo;s regions</td>
        </tr>
        <tr>
          <td>Email delivery</td>
          <td>Contact details for transactional and billing email</td>
          <td>Provider&rsquo;s regions</td>
        </tr>
      </tbody>
    </table>
    <p>
      We do not store full payment-card numbers; card details are handled directly by our payment
      processor.
    </p>

    <h2>5. Cross-border transfer of personal data</h2>
    <p>
      Sahod involves the transfer of personal data across borders: data subjects are typically
      located in the Philippines, the Service is operated by an entity in Australia, and data is
      primarily stored and processed in Singapore. One exception: our authentication and identity
      provider, which manages account sign-in, does not currently offer regional storage and
      processes account identifiers (such as email addresses and user IDs) in the United States. By using the Service, Customers and their authorised users acknowledge
      these transfers. We take reasonable steps to ensure that personal data transferred outside
      the Philippines receives a level of protection comparable to that required under RA 10173,
      including through contractual safeguards with our sub-processors.
    </p>

    <h2>6. Security</h2>
    <p>
      We maintain organisational, physical, and technical measures appropriate to the sensitivity
      of the data, including:
    </p>
    <ul>
      <li>Encryption of data in transit and at rest;</li>
      <li>Strict multi-tenant isolation so one Customer&rsquo;s data cannot be accessed by another;</li>
      <li>Role-based access controls and least-privilege permissions;</li>
      <li>
        Application-layer abuse and bot protection, and access rules enforced at the database
        layer;
      </li>
      <li>Audit logging of payroll and account events;</li>
      <li>Backup and recovery procedures.</li>
    </ul>
    <p>
      No system is perfectly secure. You are responsible for keeping your credentials confidential
      and for not sharing access with unauthorised persons.
    </p>

    <h2>7. Data breach notification</h2>
    <p>
      If we become aware of a personal data breach, we will notify the affected Customer without
      undue delay so the Customer (as Controller) can meet its obligations. Where Sahod is the
      Controller, or where required by law, we will notify the National Privacy Commission and
      affected data subjects within <strong>72 hours</strong> of knowledge of a breach likely to
      give rise to a real risk of serious harm, consistent with RA 10173 and NPC rules.
    </p>

    <h2>8. Your rights</h2>
    <p>
      Under RA 10173, data subjects have the right to be informed, to access, to object, to
      rectify, to erasure or blocking, to data portability, to damages, and to lodge a complaint
      with the National Privacy Commission. Individuals in Australia have rights of access and
      correction under the Australian Privacy Principles.
    </p>
    <ul>
      <li>
        <strong>If you are an employee</strong> whose data was entered by your employer, contact
        your employer (the Controller). We will assist the Customer in responding to your request.
      </li>
      <li>
        <strong>If you are a Customer account user</strong>, contact us using the details in
        Section 12.
      </li>
    </ul>

    <h2>9. Retention</h2>
    <p>
      For employee data, the Customer determines the retention period in line with its own legal
      obligations &mdash; for example, Philippine payroll records are commonly subject to
      statutory retention under DOLE and BIR rules. We retain such data for as long as the
      Customer&rsquo;s subscription is active and as instructed. On termination, we provide the
      Customer an opportunity to export its data, after which we delete it in accordance with our
      deletion procedures, subject to any retention required by law. Account and billing data is
      retained as long as needed for the Service and for legal, tax, and accounting purposes.
    </p>

    <h2>10. Cookies</h2>
    <p>
      We use only strictly necessary cookies &mdash; for authentication and session management.
      Blocking these may prevent parts of the Service from working.
    </p>

    <h2>11. Changes to this policy</h2>
    <p>
      We may update this Privacy Policy from time to time. We will post the current version at
      sahod.solutions/privacy and, where the change is material, notify you through the Service or
      by email. Continued use after the effective date constitutes acceptance.
    </p>

    <h2>12. Contact us</h2>
    <p>For privacy questions or to exercise your rights, contact our Data Protection Officer:</p>
    <ul>
      <li>Data Protection Officer, reachable at privacy@sahod.solutions</li>
      <li>Operating entity: Sahod.Solutions (Australia)</li>
    </ul>
    <p>
      You may also lodge a complaint with the National Privacy Commission of the Philippines
      (privacy.gov.ph) or, in Australia, the Office of the Australian Information Commissioner
      (oaic.gov.au).
    </p>
  </LegalDocShell>
);
